import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';

const ToastContext = createContext(null);

const VARIANT_BORDER = {
  error: '#FF4D00',
  success: '#F2A900',
  info: '#00D4FF',
};

const VARIANT_LABEL = {
  error: 'ERROR',
  success: 'SUCCESS',
  info: 'INFO',
};

let idCounter = 0;

function ToastItem({ toast, onDismiss }) {
  const border = VARIANT_BORDER[toast.variant] || VARIANT_BORDER.info;
  const label = VARIANT_LABEL[toast.variant] || 'INFO';
  return (
    <div
      role="status"
      className="pointer-events-auto min-w-[260px] max-w-[380px] bg-[#1E1E1E] text-[#F5F1E8] shadow-lg animate-toast-slide-in"
      style={{ borderLeft: `4px solid ${border}` }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-bold tracking-[0.14em] uppercase font-['Barlow_Condensed'] mb-0.5"
            style={{ color: border }}
          >
            {label}
          </div>
          <div className="text-sm text-[#F5F1E8] break-words">{toast.message}</div>
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
          className="text-[#A0A0A0] hover:text-[#F5F1E8] text-lg leading-none px-1 -mt-0.5"
          type="button"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export function ToastProvider({ children, duration = 4000 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (message, variant = 'info') => {
      idCounter += 1;
      const id = idCounter;
      const toast = { id, message: String(message ?? ''), variant };
      setToasts((prev) => [...prev, toast]);
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
      return id;
    },
    [dismiss, duration]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const api = {
    toast: {
      success: (msg) => push(msg, 'success'),
      error: (msg) => push(msg, 'error'),
      info: (msg) => push(msg, 'info'),
    },
    success: (msg) => push(msg, 'success'),
    error: (msg) => push(msg, 'error'),
    info: (msg) => push(msg, 'info'),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(24px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-toast-slide-in { animation: toastSlideIn 0.18s ease-out; }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
